// Custom coasters, engraved glassware and drink stirrers: a bar and event kit.
// Targets (OpenSEO US, Nov 2026): custom coasters 9,900 (KD 7, $13.90 CPC,
// Nov–Dec peak), custom coasters bulk 880, branded coasters 320 ($21.70),
// coasters with logo 210, slate/leather/wood/acrylic coasters; custom pint
// glasses 1,900, custom etched glassware 880, wine and whiskey glasses with
// logo, engraved glasses; glass engraving nyc (GSC: ~1.2K impressions over 16
// months, prtwd at 14–29 through /laser-engraving-service-nyc/, which never
// says "glass"); custom drink stirrers 1,900, personalized drink stirrers
// 1,900, swizzle sticks 720 (+48%), cocktail stirrers 390 (+46%).
// Not targeted here: "custom tumbler" (the tumblers page) and bottles (the
// bottle engraving page). Glass claims mirror those two pages: a frosted
// mark, CerMark for a dark mark, wraps quoted after a test; no rotary claim.
// Inbox, anonymized: 96 ceramic mugs UV printed overnight for an activation
// and acrylic cocktail stencils cut next day (truth.md, rush logo printing);
// a gifting agency asked about designer glasses and marble catchalls; a
// brand asked for its logo on ceramic cups.
const {
  quoted,
  setup,
  small,
  costAnswer,
  timingAnswer,
  minimumAnswer,
  ownItemsAnswer,
  shipAnswer,
  engravingItem,
} = require('./shared')

module.exports = engravingItem({
  key: 'coastersGlassware',
  slug: 'coasters-glassware',
  path: '/services/laser-engraving/coasters-glassware/',
  noun: 'bar and event kit',
  crumb: 'Coasters & glassware',
  reviewedDate: '2026-11-05',
  reviewedDateLabel: 'November 5, 2026',
  title: 'Custom Coasters, Engraved Glasses & Stirrers | PrintAWorld',
  description:
    'Custom coasters in slate, wood, leather and acrylic, engraved glassware and logo drink stirrers, made in NYC for bars, hotels, events and gifts.',
  eyebrow: 'Bar & event kit / glass engraving in NYC',
  heading:
    'Custom coasters, engraved glassware and drink stirrers with your logo.',
  answer: `PrintAWorld makes custom coasters, engraved glassware and logo drink stirrers in New York for bars, restaurants, hotels, brand activations and gifts. We laser-engrave slate, wood, leather and acrylic coasters, frost your logo into pint, wine, whiskey and rocks glasses, and cut acrylic or wood stirrers in your logo’s shape. Engraving is a ${setup} setup plus ${small}; coaster blanks, glasses and stirrers are ${quoted.toLowerCase()}, or send your own glassware.`,
  cta: 'Get a bar kit quote',
  heroImage: 'barKit',
  ideaImages: ['slateCoasters', 'etchedGlasses', 'drinkStirrers'],
  images: {
    barKit: {
      file: 'bar-kit',
      alt: 'Slate, walnut and leather coasters, a rocks glass and black acrylic stirrers, all with the same engraved circle-and-wave logo, on a walnut bar',
      caption: 'One logo on coasters, glass and stirrers',
    },
    slateCoasters: {
      file: 'slate-coasters',
      alt: 'Square black slate coasters engraved in light gray with a leaf mark and the word LOGO, a cocktail on the stack',
      caption: 'Logo coasters in slate',
    },
    etchedGlasses: {
      file: 'etched-glasses',
      alt: 'Two pint glasses and two wine glasses, each with the same small frosted engraved mountain logo',
      caption: 'Engraved pint and wine glasses',
    },
    drinkStirrers: {
      file: 'drink-stirrers',
      alt: 'Laser-cut drink stirrers in black, white and coral acrylic and birch wood, each topped with a star-burst logo shape, in a rocks glass',
      caption: 'Stirrers cut to your logo’s shape',
    },
  },
  intro: {
    eyebrow: 'Glass engraving in NYC',
    title: 'Logo coasters, etched glasses and stirrers that match.',
    paragraphs: [
      'The laser frosts your logo into the glass, so it can’t wash off like a print. The same logo goes on slate, wood, leather or acrylic coasters and on stirrers cut in its outline, so the whole bar matches.',
      'Bars and hotels order them to keep, brands and event agencies for an opening or an activation, and companies as client gifts. Send your own glasses, or tell us the style and we source them.',
      'Engraving stainless tumblers or bottles? They have their own pages: custom tumblers and bottle engraving.',
    ],
  },
  ideas: {
    eyebrow: 'Bar kit ideas',
    title: 'Slate coasters, etched glasses and logo stirrers.',
  },
  uses: {
    eyebrow: 'Who orders them',
    title: 'Branded coasters and glasses for bars, brands and events.',
    items: [
      {
        label: 'Bars and restaurants',
        title: 'Coasters and glasses with your logo',
        body: 'Slate or wood coasters for every table and etched pint glasses for the bar, reordered when you need more.',
      },
      {
        label: 'Hotels',
        title: 'Room and lounge pieces',
        body: 'Leather or wood coasters for rooms, etched glasses for the lounge, matched to your brand.',
      },
      {
        label: 'Brand activations',
        title: 'A bar kit for launches and openings',
        body: 'Coasters, glasses and stirrers with the campaign mark, for event agencies working to a date.',
      },
      {
        label: 'Client gifts',
        title: 'A boxed glass and coaster set',
        body: 'Two whiskey glasses and slate coasters with your logo, for the holidays or a closed deal.',
      },
      {
        label: 'Weddings',
        title: 'Names and the date',
        body: 'Engraved glasses for the wedding party and coasters or stirrers with your names for every guest.',
      },
    ],
  },
  materials: {
    link: 'Which materials',
    eyebrow: 'Which materials',
    title: 'Slate, wood, leather, acrylic and glass.',
    items: [
      {
        title: 'Slate and stone',
        body: 'Natural slate and marble coasters engrave to a light gray mark on dark stone.',
      },
      {
        title: 'Wood and leather',
        body: 'Walnut, maple or bamboo coasters engrave dark brown; leather a shade darker than the hide.',
      },
      {
        title: 'Glass and ceramic',
        body: 'Pint, wine, whiskey and rocks glasses take a frosted white mark; ceramic mugs can be UV printed in full color.',
      },
      {
        title: 'Acrylic',
        body: 'Coasters and stirrers laser cut from opaque acrylic in your shape, or a full-color logo UV printed on flat coasters.',
      },
    ],
  },
  process: {
    title: 'From your logo to a bar that matches.',
    note: 'Standard lead time is 2–3 weeks after the glasses or blanks reach us. For holiday parties and December events, send the request in November.',
    steps: [
      {
        title: 'Send the logo and the list',
        body: 'Your logo, what you need (coasters, glasses, stirrers), the materials, the count and the date.',
      },
      {
        title: 'Approve a test',
        body: 'We send a proof and engrave a spare glass or coaster, so you see the real mark first.',
      },
      {
        title: 'We make the run',
        body: 'Each piece engraved in the same spot and checked, with names matched to your list.',
      },
      {
        title: 'Packed for the bar or event',
        body: 'Boxed by type, shipped anywhere in the U.S., picked up in NYC or couriered to your venue.',
      },
    ],
  },
  pricing: {
    title: 'Custom coaster and glass engraving pricing.',
    cta: 'Price my bar kit',
    rows: [
      { label: 'Engraving, per coaster or glass', value: small },
      { label: 'Coaster blanks: slate, wood, leather, acrylic', value: quoted },
      { label: 'Glasses, if we source them', value: quoted },
      { label: 'Drink stirrers cut to your logo', value: quoted },
      { label: 'A second side or a wrap', value: quoted },
      { label: 'Rush for an event', value: 'Case by case' },
    ],
  },
  bulk: {
    eyebrow: 'Custom coasters in bulk',
    title: 'Hundreds of coasters and glasses for an opening, on one logo.',
    paragraphs: [
      'For bulk runs we engrave and approve one piece, then the rest on the same setup. Event agencies work to tight dates here: a full-color logo was UV printed on 96 ceramic mugs overnight for an activation, and acrylic logo cocktail stencils were laser cut the next day.',
      'A gifting agency asked us to personalize sets of designer glasses and marble catchalls, and a brand sent ceramic cups for its logo. Send what you have; we test one, then run the set.',
    ],
    cta: 'Plan a bulk order',
  },
  faqTitle: 'Coaster and glass engraving questions, answered.',
  faqs: [
    {
      question: 'How much do custom coasters and engraved glasses cost?',
      answer: costAnswer(
        small,
        `Coaster blanks, glasses, stirrers, a second side and a wrap are ${quoted.toLowerCase()}.`
      ),
    },
    {
      question: 'Do you do glass engraving in NYC?',
      answer: `Yes. We engrave glasses in our New York studio. ${ownItemsAnswer('glasses')}`,
    },
    {
      question: 'What does an engraved glass look like?',
      answer:
        'A frosted white mark in the glass. For a dark mark, we apply a marking compound (CerMark) before engraving. Color means UV printing, which we quote separately.',
    },
    {
      question: 'Can you engrave all the way around a glass?',
      answer:
        'Often, depending on the glass shape and stem. Wraparound and two-sided designs are quoted per project after a test.',
    },
    {
      question: 'Which coaster material should I choose?',
      answer:
        'Slate looks upscale and shows a light gray mark. Wood and leather feel warm and engrave darker. Acrylic can be cut to your outline or printed in full color. We test the material you pick before the run.',
    },
    {
      question: 'Can you make custom drink stirrers and swizzle sticks?',
      answer:
        'Yes. We laser cut stirrers from acrylic or wood, with the top in your logo’s outline or a name on each. If your venue needs certified food-contact material, tell us before we quote.',
    },
    {
      question: 'Do you engrave tumblers and bottles too?',
      answer:
        'Yes, on their own pages: custom tumblers for YETI, Stanley and stainless drinkware, and bottle engraving for wine, champagne and liquor bottles.',
    },
    {
      question: 'Is there a minimum order?',
      answer: minimumAnswer('coaster or glass'),
    },
    {
      question: 'How long does it take?',
      answer: timingAnswer(
        'For holiday parties and December events, send the request in November.'
      ),
    },
    {
      question: 'Do you ship?',
      answer: shipAnswer,
    },
  ],
  quote: {
    title: 'Tell us about your bar kit.',
    body: 'Send the logo, what you need, how many and when you need it. We’ll reply with a written quote for each quantity.',
    checklist: [
      'Your logo (vector if you have it) or the names',
      'Coasters, glasses, stirrers: the materials and styles',
      'How many of each, or several quantities to price',
      'Your in-hand or event date and delivery ZIP',
    ],
  },
  moreCards: ['tumblers', 'bottleEngraving', 'corporateGifts'],
  extraRelated: [
    ['Custom tumblers', '/services/laser-engraving/tumblers/'],
    ['Bottle engraving', '/services/laser-engraving/bottle-engraving/'],
    ['Rush logo printing', '/services/uv-printing/rush-logo-printing/'],
    ['Live event engraving', '/services/on-site-laser-engraving-nyc/'],
  ],
  schema: {
    name: 'Custom Coasters, Engraved Glassware and Drink Stirrers',
    alternateName: [
      'Custom coasters',
      'Branded coasters',
      'Coasters with logo',
      'Custom pint glasses',
      'Custom etched glassware',
      'Glass engraving NYC',
      'Custom drink stirrers',
      'Swizzle sticks',
    ],
    serviceType: 'Laser-engraved coasters, glassware and drink stirrers',
  },
})
